import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useAPI } from '../../api';
import PageSection from '../../components/PageSection';
import PropEditor from '../../components/PropEditor';
import DataTable from '../../components/DataTable';
import RawJsonDisplay from '../../components/RawJsonDisplay';
import Avatar from '../../components/Avatar';
import { AuthInfo, AccessGrant } from 'types';
import { OrgContextLink, useOrgContext } from 'components/OrgContextProvider';
import { useDataCache, useRoles } from 'components/DataCacheProvider';

interface UserInfo {
	id?: string;
	display?: string;
	username?: string;
	status?: string;
	auth?: AuthInfo;
	access?: AccessGrant[];
}

interface ChangePasswordState {
	password: string;
	confirmPassword: string;
}

const UserContainer = () => {
	const api = useAPI();
	const ctx = useOrgContext();

	const { userID } = useParams();

	const [userInfo, setUserInfo] = useState<UserInfo>({});
	const [newAvatar, setNewAvatar] = useState<File | null>(null);
	const { roles: rolesCache, loadRoles } = useDataCache();

	// Build context options from availableTenants (system tenant is synthetic, only present for system admins)
	const contextOptions: { label: string; tenantID: string; workspaceID: string | null }[] = [];
	for (const tenant of ctx?.availableTenants ?? []) {
		if (tenant.id === 'system') {
			contextOptions.push({ label: 'System', tenantID: 'system', workspaceID: null });
		} else {
			contextOptions.push({ label: `Tenant: ${tenant.name}`, tenantID: tenant.id, workspaceID: null });
			for (const workspace of tenant.workspaces) {
				contextOptions.push({ label: `Workspace: ${workspace.name} (${tenant.name})`, tenantID: tenant.id, workspaceID: workspace.id });
			}
		}
	}
	const contextKey = (tenantID: string, workspaceID: string | null) => `${tenantID}:${workspaceID ?? ''}`;

	const [newGrant, setNewGrant] = useState({ tenantID: contextOptions[0]?.tenantID ?? '', workspaceID: contextOptions[0]?.workspaceID ?? null as string | null, role: '' });

	const [changePasswordState, setChangePasswordState] = useState<ChangePasswordState>({ password: '', confirmPassword: '' });

	const dropdownRoles = useRoles(newGrant.tenantID);

	const loadUser = () => {
		api.get<UserInfo>('/api/users/' + userID).then(json => {
			setUserInfo(json);
		});
	};

	useEffect(() => {
		api.get<UserInfo>('/api/users/' + userID).then(json => {
			setUserInfo(json);
			const tenantIDs = [...new Set((json.access ?? []).map((g: AccessGrant) => g.tenantID))];
			for (const tid of tenantIDs) loadRoles(tid);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const addAccessGrant = () => {
		if (!newGrant.tenantID) return alert('Select a context');
		if (!newGrant.role) return alert('Select a role');

		api.put(`/api/users/${userID}/access`, { tenantID: newGrant.tenantID, workspaceID: newGrant.workspaceID, role: newGrant.role })
		.then(loadUser);
	};

	const deleteAccessGrant = (grant: AccessGrant) => {
		const roleName = rolesCache[grant.tenantID]?.find(r => r.id === grant.role)?.name ?? grant.role;
		const tenantName = grant.tenantID === 'system' ? 'System' : ctx?.availableTenants?.find(t => t.id === grant.tenantID)?.name ?? grant.tenantID;
		const workspaceName = grant.workspaceID ? (ctx?.availableTenants?.find(t => t.id === grant.tenantID)?.workspaces?.find(w => w.id === grant.workspaceID)?.name ?? grant.workspaceID) : null;
		const grantDescription = workspaceName ? `${workspaceName} (${tenantName}) - ${roleName}` : `${tenantName} - ${roleName}`;

		if (window.confirm(`Delete access grant: ${grantDescription}?`)) {
			api.delete(`/api/users/${userID}/access`, {
				method: 'DELETE',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ tenantID: grant.tenantID, workspaceID: grant.workspaceID ?? null })
			})
			.then(loadUser);
		}
	};

	const changePassword = () => {
		if (changePasswordState.password !== changePasswordState.confirmPassword)
			return alert('Passwords do not match');

		api.put(`/api/users/${userID}/password`, { password: changePasswordState.password })
		.then(() => alert('Changed password'));
	};

	const disable2FA = () => {
		if (window.confirm(`Disable 2FA for ${userInfo.username}?`)) {
			api.post(`/api/users/${userInfo.id}/twofactor/disable`, {})
			.then(() => loadUser());
		}
	};

	const resetAvatar = () => {
		if (window.confirm(`Reset avatar for ${userInfo.username}?`)) {
			api.post(`/api/users/${userInfo.id}/avatar/reset`, {})
			.then(() => {
				reloadAvatar();
				loadUser();
			});
		}
	};

	const reloadAvatar = async () => {
		await Promise.all([
			fetch(`/api/users/public/${userID}/avatar`, { cache: 'reload' }),
			fetch(`/api/users/public/${userID}/avatar?size=small`, { cache: 'reload' }),
			fetch(`/api/users/public/${userID}/avatar?size=medium`, { cache: 'reload' }),
			fetch(`/api/users/public/${userID}/avatar?size=large`, { cache: 'reload' })
		]);

		document.querySelectorAll<HTMLImageElement>('img.avatar').forEach(img => {
			const oldSrc = img.src;
			img.src = '';
			setTimeout(() => img.src = oldSrc, 100);
		});
	};

	return (
		<div>
			<PageSection title={`User: ${userInfo?.username ?? ''}`} level={2} />

			<PageSection borderEnd={true}>
				<OrgContextLink to="/settings/users">&#x25c0; Back to Users</OrgContextLink>
			</PageSection>

			<PageSection title="Avatar" level={3}>
				<div>
					<div style={{ display: 'inline-block', marginRight: '1em', verticalAlign: 'top' }}>
						<p>Large</p>
						<Avatar src={`/api/users/public/${userID}/avatar?size=large`} alt="avatar" size="256px" radius="32px" />
					</div>
					<div style={{ display: 'inline-block', marginRight: '1em', verticalAlign: 'top' }}>
						<p>Medium</p>
						<Avatar src={`/api/users/public/${userID}/avatar?size=medium`} alt="avatar" size="128px" radius="16px" />
					</div>
					<div style={{ display: 'inline-block', marginRight: '1em', verticalAlign: 'top' }}>
						<p>Small</p>
						<Avatar src={`/api/users/public/${userID}/avatar?size=small`} alt="avatar" size="48px" radius="8px" />
					</div>
				</div>

				<p>
					<input type="file" accept="image/*" onChange={e => {
						const file = e.target.files?.[0];
						if (file) setNewAvatar(file);
					}} />

					{
						newAvatar &&
						(
							<button onClick={() => {
								api.upload(`/api/users/${userID}/avatar`, newAvatar)
								.then(() => {
									reloadAvatar();
									setNewAvatar(null);
								});
							}}>Upload Avatar</button>
						)
					}
				</p>

				<p>
					<button onClick={resetAvatar}>Reset Avatar</button>
				</p>
			</PageSection>

			<PageSection title="User Info" level={3}>
				<PropEditor<UserInfo>
					object={userInfo}
					onChange={setUserInfo}
					props={[
						{ label: 'Display Name', key: 'display' },
						{ label: 'Username', key: 'username', disabled: true },
						{ label: 'Status', key: 'status', type: 'select', disabled: true, options: [{ label: 'Inactive', value: 'inactive' }, { label: 'Active', value: 'active' }, { label: 'Invited', value: 'invited' }] },
					]}
				/>
			</PageSection>

			<PageSection title="Auth" level={3}>
				<PropEditor<AuthInfo>
					object={userInfo.auth ?? {} as AuthInfo}
					onChange={(newAuth) => setUserInfo({ ...userInfo, auth: newAuth })}
					props={[
						{ label: 'Strategies', key: 'strategies', type: 'text', disabled: true },
						{ label: 'Last Strategy', key: 'lastStrategy', type: 'text', disabled: true },
						{ label: '2FA', key: 'twoFactor.enabled', type: 'checkbox', disabled: true },
						{ label: '2FA Type', key: 'twoFactor.type', type: 'text', disabled: true }
					]}
				/>

				<p>
					<DataTable
						data={userInfo.auth?.strategies?.map(strategy => ({ strategy, lastUsed: strategy === userInfo.auth?.lastStrategy })) ?? []}
						columns={[
							{ label: 'Strategy', key: 'strategy' },
							{ label: 'Last Used', key: 'lastUsed', render: (row) => row.lastUsed ? 'Yes' : 'No' }
						]}
					/>
				</p>
			</PageSection>

			<PageSection title="Two-Factor Authentication" level={3}>
				{
					userInfo && userInfo.auth?.twoFactor?.enabled ? (
						<div>
							<p>Two-factor authentication is enabled</p>

							<button onClick={disable2FA}>Disable 2FA</button>
						</div>
					) : (
						<div>
							<p>Two-factor authentication is not enabled</p>
						</div>
					)
				}
			</PageSection>

			<PageSection title="Access Grants" level={3}>
				<DataTable<AccessGrant>
					data={userInfo.access ?? []}
					columns={[
						{ label: 'Tenant', key: 'tenantID', render: (grant) => {
							if (grant.tenantID === 'system') return 'System';
							const tenant = ctx?.availableTenants?.find(t => t.id === grant.tenantID);
							return tenant?.name ?? grant.tenantID;
						}},
						{ label: 'Workspace', key: 'workspaceID', render: (grant) => {
							if (!grant.workspaceID) return '-';
							const tenant = ctx?.availableTenants?.find(t => t.id === grant.tenantID);
							const workspace = tenant?.workspaces?.find(w => w.id === grant.workspaceID);
							return workspace?.name ?? grant.workspaceID;
						}},
						{ label: 'Role', key: 'role', render: (grant) => rolesCache[grant.tenantID]?.find(r => r.id === grant.role)?.name ?? grant.role },
					]}
					tools={[
						{ label: 'Delete', render: (grant) => <button className="link" onClick={() => deleteAccessGrant(grant)}>Delete</button> }
					]}
				/>

				<p>
					<select
						value={contextKey(newGrant.tenantID, newGrant.workspaceID)}
						onChange={e => {
							const opt = contextOptions.find(o => contextKey(o.tenantID, o.workspaceID) === e.target.value);
							if (opt) setNewGrant(g => ({ ...g, tenantID: opt.tenantID, workspaceID: opt.workspaceID, role: '' }));
						}}
					>
						{contextOptions.map(opt => (
							<option key={contextKey(opt.tenantID, opt.workspaceID)} value={contextKey(opt.tenantID, opt.workspaceID)}>
								{opt.label}
							</option>
						))}
					</select>
					{' '}
					<select value={newGrant.role} onChange={e => setNewGrant(g => ({ ...g, role: e.target.value }))}>
						<option value="">-- Select Role --</option>
						{dropdownRoles.map(role => (
							<option key={role.id} value={role.id}>{role.name}</option>
						))}
					</select>
					{' '}
					<button onClick={addAccessGrant}>Add Grant</button>
				</p>
			</PageSection>

			<PageSection title="Change Password" level={3}>
				<PropEditor<ChangePasswordState>
					object={changePasswordState}
					onChange={setChangePasswordState}
					props={[
						{ label: 'Password', key: 'password', type: 'password' },
						{ label: 'Confirm Password', key: 'confirmPassword', type: 'password' }
					]}
					tools={[
						{ label: 'Change Password', render: () => <input type="button" value="Change Password" onClick={changePassword} /> }
					]}
				/>
			</PageSection>

			<PageSection title="Raw Data" level={3}>
				<RawJsonDisplay data={userInfo} />
			</PageSection>
		</div>
	);
};

export default UserContainer;
